// Mock data for Naabu AI Website

export const navigationLinks = [
  { name: 'How it works', href: '/#how-it-works' },
  { name: 'Agents', href: '/agents' },
];

export const heroData = {
  headline: 'One prompt. A full AI team.',
  subheadline: 'Naabu is your AI Manager that automatically assigns the right specialists to get real work done.',
  primaryCta: 'Pre-register now',
  secondaryCta: 'See how it works →',
};

export const problemData = {
  title: "Startups don't need more tools. They need a team.",
  description: 'Small teams and startups are expected to do everything:',
  tasks: ['Strategy', 'Data analysis', 'Sales', 'Marketing', 'Content', 'Hiring'],
  insight: "Hiring a full team is expensive. Managing multiple tools is chaotic. That's where Naabu comes in.",
  image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f',
};

export const solutionData = {
  title: 'Naabu thinks like a manager.',
  description: 'Naabu is not just another AI tool. Naabu is an AI Manager. You give Naabu one clear goal.',
  steps: [
    { text: 'Understands your objective', icon: 'Search' },
    { text: 'Selects the right AI agents', icon: 'Brain' },
    { text: 'Coordinates them automatically', icon: 'Cog' },
    { text: 'Delivers actionable results', icon: 'CheckCircle' },
  ],
  closing: 'No micromanagement. No prompt chaos. Just execution.',
  image: 'https://images.unsplash.com/photo-1655890006065-edefcd764af6',
};

export const howItWorksData = {
  title: 'How it works',
  steps: [
    {
      number: '01',
      title: 'Define the goal',
      examples: ['"I want more customers"', '"Build a go-to-market strategy"', '"Improve my sales funnel"'],
    },
    {
      number: '02',
      title: 'Naabu builds the team',
      description: 'Naabu decides which agents are needed and how they should collaborate.',
    },
    {
      number: '03',
      title: 'Get results',
      description: 'Clear strategies, content, plans, analyses, and next steps, ready to use.',
    },
  ],
  image: 'https://images.unsplash.com/photo-1674027444485-cec3da58eef4',
};

export const featuresData = {
  title: 'More than agents. A system.',
  features: [
    {
      icon: 'Cog',
      title: 'Workflow Automations',
      description: 'Automate recurring tasks and processes across agents.',
      image: 'https://images.unsplash.com/photo-1689443111384-1cf214df988a',
    },
    {
      icon: 'Puzzle',
      title: 'Build Your Own Agents',
      description: 'Create custom agents tailored to your business or workflow.',
      image: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg',
    },
  ],
};

export const audienceData = {
  title: 'Built for lean teams with big ambitions.',
  audiences: [
    { name: 'Startups', icon: 'Rocket' },
    { name: 'Solo founders', icon: 'User' },
    { name: 'Small teams', icon: 'Users' },
    { name: 'Agencies', icon: 'Building2' },
    { name: 'Product builders', icon: 'Hammer' },
  ],
  closing: "If you can't hire a full team, now you don't have to.",
  image: 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5',
};

export const agentsData = [
  {
    id: 'sam',
    name: 'Strategic Sam',
    emoji: '🧠',
    role: 'Business Strategy',
    description: 'Defines vision, positioning, growth plans, and overall direction.',
    color: '#f06a15',
  },
  {
    id: 'dana',
    name: 'Data Dana',
    emoji: '📊',
    role: 'Data Analyst',
    description: 'Analyzes performance, insights, trends, and metrics to guide decisions.',
    color: '#3b82f6',
  },
  {
    id: 'ella',
    name: 'Email Ella',
    emoji: '✉️',
    role: 'Email Marketing',
    description: 'Creates campaigns, flows, sequences, and optimizes open & conversion rates.',
    color: '#10b981',
  },
  {
    id: 'sean',
    name: 'Sales Sean',
    emoji: '💰',
    role: 'Sales Agent',
    description: 'Practical sales execution: scripts, funnels, objections, outreach.',
    color: '#f59e0b',
  },
  {
    id: 'ray',
    name: 'Recruit Ray',
    emoji: '🧲',
    role: 'Recruitment',
    description: 'Hiring strategies, job descriptions, candidate evaluation.',
    color: '#8b5cf6',
  },
  {
    id: 'sara',
    name: 'Social Sara',
    emoji: '📱',
    role: 'Social Media',
    description: 'Content planning, posting strategies, growth and engagement.',
    color: '#ec4899',
  },
  {
    id: 'chris',
    name: 'Copy Chris',
    emoji: '✍️',
    role: 'Copywriting & SEO',
    description: 'High-converting copy, landing pages, blogs, and SEO optimization.',
    color: '#06b6d4',
  },
  {
    id: 'gio',
    name: 'Geo Gio',
    emoji: '🌍',
    role: 'GEO (GPT Engine Optimizer)',
    description: 'Optimizes content and structure for AI search & GPT-based discovery.',
    color: '#84cc16',
  },
  {
    id: 'vera',
    name: 'VA Vera',
    emoji: '🗂',
    role: 'Virtual Assistant',
    description: 'Planning, summaries, agendas, task management, inbox support.',
    color: '#f43f5e',
  },
];

export const pricingData = {
  header: {
    title: 'Simple pricing. An entire AI team included.',
    subtitle: 'No per-agent fees. No hidden costs. One manager. All agents. Choose monthly flexibility or save big with annual billing.',
  },
  monthly: {
    price: '$60',
    period: '/ month',
    features: [
      'Full access to Naabu AI Manager',
      'All AI agents included',
      'Unlimited goals & prompts',
      'No long-term commitment',
    ],
    bestFor: 'Trying Naabu or short-term projects',
    cta: 'Start Monthly',
  },
  yearly: {
    regularPrice: '$600',
    offerPrice: '$180',
    perMonth: '$15',
    savings: '70%',
    features: [
      'Everything in Monthly',
      'Full AI team access',
      'Workflow automations',
      'Priority execution',
    ],
    bestFor: 'Startups & teams serious about growth',
    cta: 'Start Yearly & Save 70%',
  },
  comparison: [
    { feature: 'AI Manager (Naabu)', monthly: true, yearly: true },
    { feature: 'All Agents', monthly: true, yearly: true },
    { feature: 'Unlimited Prompts', monthly: true, yearly: true },
    { feature: 'Workflow Automations', monthly: false, yearly: true },
    { feature: 'Priority Processing', monthly: false, yearly: true },
    { feature: 'Discount', monthly: false, yearly: '70% first year' },
  ],
  trust: {
    points: ['No surprises. Cancel anytime', 'No per-agent fees', 'No hidden costs'],
    closing: 'One subscription = an entire AI team.',
  },
};
